import type { TFunction } from "i18next";

import { currentLocale } from "../i18n";
import type { SqlBoolean } from "./types";

/**
 * Timestamps arrive as ISO 8601 in the app's timezone ("Bern"). Formatting is
 * left to Intl so it follows the locale the user picked, without pulling in a
 * date library — antd's own dayjs is only used inside its components.
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(currentLocale(), {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

/**
 * Plain dates ("2026-08-04") — no time part, so none is rendered. Parsed by
 * hand rather than through Date: `new Date("2026-08-04")` is read as UTC
 * midnight and would show the previous day west of Greenwich.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "";

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;

  const [, year, month, day] = match;
  return new Intl.DateTimeFormat(currentLocale(), { dateStyle: "short" }).format(
    new Date(Number(year), Number(month) - 1, Number(day)),
  );
}

/**
 * "Schema: 12 · Nahe bei Gott" — the number of the outline and the title of the
 * talk read as one thing, so they are shown as one. As two columns the number
 * sat alone in a narrow right-aligned cell, a congregation and a speaker away
 * from the title it names.
 *
 * Either half can be missing: a week with no talk has neither, and an event the
 * congregation wrote itself has a title and no number.
 */
export function speechLabel(
  schema: string | null | undefined,
  title: string | null | undefined,
  t: TFunction,
): string | null {
  const parts = [
    schema ? `${t("activerecord.attributes.speech.schema")}: ${schema}` : null,
    title || null,
  ].filter(Boolean);

  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Every number the speaker's account carries, in the order the settings show
 * them. Which of the two a speaker keeps is up to them, so a row can hold none,
 * one or both, and the coordinator ringing about next Sunday wants whichever
 * one answers.
 */
export function speakerPhones(row: {
  speaker_phone?: string | null;
  speaker_mobile?: string | null;
}): string[] {
  return [row.speaker_phone, row.speaker_mobile].filter((number): number is string =>
    Boolean(number),
  );
}

/** See SqlBoolean: the raw SQL rows carry MySQL's 0/1 instead of false/true. */
export function toBoolean(value: SqlBoolean | undefined): boolean {
  return value === true || value === 1 || value === "1";
}

/** Sorts numeric strings ("10" before "9") and keeps empty values last. */
export function compareNumeric(a: string | null, b: string | null): number {
  const left = Number(a);
  const right = Number(b);

  if (Number.isNaN(left) && Number.isNaN(right)) return compareText(a, b);
  if (Number.isNaN(left)) return 1;
  if (Number.isNaN(right)) return -1;

  return left - right;
}

export function compareText(a: string | null, b: string | null): number {
  return (a ?? "").localeCompare(b ?? "", currentLocale());
}
